/**
 * Workflow Redux Slice
 * Tracks workflow step progression
 * Migrated from rate-loader-abyss-migration
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type WorkflowStage = 'columnMapping' | 'validation' | 'load';

export interface WorkflowState {
  currentStep: WorkflowStage;
  completedSteps: WorkflowStage[];
  isTransitioning: boolean;
  showStepper: boolean;
  highestWorkflowStage: WorkflowStage | null;
}

const initialState: WorkflowState = {
  currentStep: 'columnMapping',
  completedSteps: [],
  isTransitioning: false,
  showStepper: false,
  highestWorkflowStage: null,
};

/** Stage ordering for comparison */
const STAGE_ORDER: Record<WorkflowStage, number> = {
  columnMapping: 0,
  validation: 1,
  load: 2,
};

const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<WorkflowStage>) => {
      state.currentStep = action.payload;
      // Update highest stage if needed
      if (!state.highestWorkflowStage ||
        STAGE_ORDER[action.payload] > STAGE_ORDER[state.highestWorkflowStage]) {
        state.highestWorkflowStage = action.payload;
      }
    },
    completeStep: (state, action: PayloadAction<WorkflowStage>) => {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
    },
    moveToNextStep: (state) => {
      const stages: WorkflowStage[] = ['columnMapping', 'validation', 'load'];
      const currentIndex = stages.indexOf(state.currentStep);
      if (currentIndex < stages.length - 1) {
        state.currentStep = stages[currentIndex + 1];
      }
    },
    setShowStepper: (state, action: PayloadAction<boolean>) => {
      state.showStepper = action.payload;
    },
    setTransitioning: (state, action: PayloadAction<boolean>) => {
      state.isTransitioning = action.payload;
    },
    resetWorkflow: () => {
      return initialState;
    },
  },
});

export const {
  setCurrentStep,
  completeStep,
  moveToNextStep,
  setShowStepper,
  setTransitioning,
  resetWorkflow,
} = workflowSlice.actions;

export default workflowSlice.reducer;
